import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { API_URL } from "@/shared"
import { showNotification } from "@mantine/notifications"
import { Doctor } from "../domain/doctor.types"

export const useDoctors = () => {
  return useQuery<Doctor[]>({
    queryKey: ["doctors"],
    queryFn: async () => {
      try {
        const response = await axios.get(`${API_URL}/doctors`)
        return response.data.data
      } catch (err) {
        console.error("Error getting doctors", err)
        showNotification({ title: "Error al obtener médicos", message: "No se pudieron cargar los médicos. Inténtelo nuevamente.", color: "red" })
        throw err
      }
    }
  })
}

export const useCreateDoctor = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (doctorData: Partial<Doctor>) => {
      try {
        const response = await axios.post(`${API_URL}/doctors`, doctorData)
        showNotification({ title: "Médico agregado", message: "El médico ha sido agregado exitosamente.", color: "green" })
        return response.data.data
      } catch (err) {
        console.error("Error creating doctor", err)
        showNotification({ title: "Error al agregar médico", message: "No se pudo agregar el médico. Inténtelo nuevamente.", color: "red" })
        throw err
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["doctors"] })
    }
  })
}

export const useUpdateDoctor = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, doctorData }: { id: string; doctorData: Partial<Doctor> }) => {
      try {
        const response = await axios.put(`${API_URL}/doctors/${id}`, doctorData)
        showNotification({ title: "Médico actualizado", message: "El médico ha sido actualizado exitosamente.", color: "green" })
        return response.data.data
      } catch (err) {
        console.error("Error updating doctor", err)
        showNotification({ title: "Error al actualizar médico", message: "No se pudo actualizar el médico. Inténtelo nuevamente.", color: "red" })
        throw err
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["doctors"] })
    }
  })
}

export const useDeleteDoctor = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        const response = await axios.delete(`${API_URL}/doctors/${id}`)
        showNotification({ title: "Médico eliminado", message: "El médico ha sido eliminado exitosamente.", color: "green" })
        return response.data
      } catch (err) {
        console.error("Error deleting doctor", err)
        showNotification({ title: "Error al eliminar médico", message: "No se pudo eliminar el médico. Inténtelo nuevamente.", color: "red" })
        throw err
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["doctors"] })
    }
  })
}

export const useDoctorMasters = () => {
  return useQuery({
    queryKey: ["doctor-masters"],
    queryFn: async () => {
      try {
        const response = await axios.get(`${API_URL}/doctors/masters`)
        return response.data.data
      } catch (err) {
        console.error("Error getting masters", err)
        throw err
      }
    }
  })
}
